import React from 'react';
import Skeleton from './Skeleton';

/**
 * Shimmer placeholder for the Visit Details modal.
 *
 * Mirrors the real layout block for block — status banner, the two-column
 * assigned/tasks + planned-time grid, and the full-width location panel with
 * its map beside the clock-in/out cards. Matching the shape keeps the modal
 * from jumping as the data lands.
 */
const VisitDetailsSkeleton = () => (
  <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
    {/* Status banner */}
    <Skeleton className="h-12 w-full rounded-xl" />

    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {/* Assigned + tasks */}
      <div className="space-y-4">
        <div>
          <Skeleton className="mb-2 h-2.5 w-16 rounded" />
          <div className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-800 p-3">
            <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3.5 w-28 rounded" />
              <Skeleton className="h-2.5 w-36 rounded" />
            </div>
            <Skeleton className="h-7 w-20 rounded-lg" />
          </div>
        </div>

        <div>
          <Skeleton className="mb-2 h-2.5 w-12 rounded" />
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-2">
                <Skeleton className="h-2 w-2 shrink-0 rounded-full" />
                {/* Staggered widths so it reads as a list, not a stack of bars. */}
                <Skeleton className={`h-3 rounded ${['w-40', 'w-32', 'w-36'][i]}`} />
                <Skeleton className="ml-auto h-3 w-14 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Planned & actual time */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
        <Skeleton className="mb-3 h-2.5 w-28 rounded" />
        <div className="grid grid-cols-2 gap-2">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2">
              <Skeleton className="h-2 w-16 rounded" />
              <Skeleton className="mt-1.5 h-3 w-20 rounded" />
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Location — map beside the clock-in / clock-out cards */}
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
      <Skeleton className="mb-2 h-2.5 w-16 rounded" />
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="sm:w-1/2">
          <Skeleton className="h-[172px] w-full rounded-xl" />
        </div>
        <div className="flex flex-1 flex-col gap-2">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-800 px-3.5 py-3"
            >
              <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3.5 w-36 rounded" />
                <Skeleton className="h-2.5 w-24 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-2 flex gap-4">
        <Skeleton className="h-2.5 w-28 rounded" />
        <Skeleton className="h-2.5 w-16 rounded" />
        <Skeleton className="h-2.5 w-16 rounded" />
      </div>
    </div>
  </div>
);

export default VisitDetailsSkeleton;
